import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { authAPI, invitesAPI, organisationsAPI, workspaceAPI } from '../lib/api';
import { useAuthStore } from '../stores/authStore';

// The account: its members (paid seats) and their roles, pending invites, and
// its workspaces. Guests are managed per workspace, in Workspace Settings.
//
// Roles: an owner pays and can do everything; an admin manages members and
// workspaces; a member works in every workspace. The server enforces all of it;
// this page only hides what would be refused.

const ROLE_LABELS = { owner: 'Owner', admin: 'Admin', member: 'Member' };
const RANK = { member: 1, admin: 2, owner: 3 };

const card = 'bg-[#111111] border border-[#2a2a2a] rounded-lg p-6 mb-6';
const heading = 'text-sm font-semibold text-gray-400 uppercase tracking-wider mb-4';
const input = 'bg-[#0a0a0a] border border-[#2a2a2a] rounded px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-blue-500';
const primaryBtn = 'px-3 py-2 bg-blue-700 hover:bg-blue-600 disabled:opacity-40 text-white text-xs font-medium rounded transition-colors whitespace-nowrap';
const quietBtn = 'px-3 py-2 bg-[#1a1a1a] hover:bg-[#222] border border-[#2a2a2a] rounded text-xs text-gray-300 transition-colors whitespace-nowrap';

function money({ amount, currency }) {
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency, maximumFractionDigits: 0 }).format(amount);
}

function Avatar({ person }) {
  if (person.avatar_url) return <img src={person.avatar_url} alt="" className="w-7 h-7 rounded-full flex-shrink-0" />;
  return (
    <div className="w-7 h-7 rounded-full bg-[#2a2a2a] flex items-center justify-center flex-shrink-0">
      <span className="text-xs text-gray-400 font-medium">{(person.name || person.email)[0].toUpperCase()}</span>
    </div>
  );
}

/** The link from a new invite. Shown once — only its hash is stored. */
export function IssuedInviteLink({ issued, onDone }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(issued.link);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard unavailable — the field is select-all anyway */ }
  }
  return (
    <div className="p-3 bg-blue-950/40 border border-blue-800 rounded space-y-2">
      <div className="text-xs text-blue-300">
        Send this link to <span className="text-white">{issued.email}</span>. It works once, only for that address,
        for 14 days. It will not be shown again.
      </div>
      <div className="flex gap-2">
        <code className="flex-1 bg-[#0a0a0a] border border-[#2a2a2a] rounded px-3 py-2 text-xs text-blue-400 font-mono select-all break-all">
          {issued.link}
        </code>
        <button onClick={copy} className={`${quietBtn} self-start`}>{copied ? 'Copied' : 'Copy'}</button>
      </div>
      <button onClick={onDone} className="text-xs text-gray-500 hover:text-white transition-colors">Done</button>
    </div>
  );
}

function CreateOwnAccount({ onCreated }) {
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  async function submit(e) {
    e.preventDefault();
    if (!name.trim()) return;
    setSaving(true);
    setError('');
    try {
      const r = await organisationsAPI.create(name.trim());
      onCreated(r.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not create the account');
      setSaving(false);
    }
  }

  return (
    <div className={card}>
      <h2 className={heading}>Set up your own account</h2>
      <p className="text-sm text-gray-400 mb-2 leading-relaxed">
        You don&rsquo;t have an account of your own — you&rsquo;re working as a guest in other people&rsquo;s.
      </p>
      <p className="text-xs text-gray-500 mb-4 leading-relaxed">
        Your own account lets you create shows and workspaces and invite people. It starts with a 14-day free trial.
        Once it&rsquo;s paid for, you also get full production access wherever you&rsquo;re a guest.
      </p>
      <form onSubmit={submit} className="flex gap-2">
        <input value={name} onChange={e => setName(e.target.value)} placeholder="Account name" className={`flex-1 ${input}`} />
        <button type="submit" disabled={saving || !name.trim()} className={primaryBtn}>
          {saving ? 'Creating…' : 'Create account'}
        </button>
      </form>
      {error && <p className="text-xs text-red-400 mt-2">{error}</p>}
    </div>
  );
}

export default function Account() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const user = useAuthStore(state => state.user);
  const setAuth = useAuthStore(state => state.setAuth);

  const [accounts, setAccounts] = useState(null);
  const [account, setAccount] = useState(null);
  const [invites, setInvites] = useState([]);
  const [error, setError] = useState('');

  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  const [inviting, setInviting] = useState(false);
  const [issued, setIssued] = useState(null);

  const [newWorkspace, setNewWorkspace] = useState('');
  const [renaming, setRenaming] = useState(false);
  const [nameDraft, setNameDraft] = useState('');

  const selectedId = params.get('org')
    || accounts?.find(a => a.id === user?.organisation_id)?.id
    || accounts?.[0]?.id
    || null;

  const refreshSession = useCallback(async () => {
    const r = await authAPI.me();
    const { token, ...me } = r.data;
    setAuth(token, me);
  }, [setAuth]);

  const loadAccounts = useCallback(() => {
    organisationsAPI.mine()
      .then(r => setAccounts(r.data))
      .catch(() => setError('Failed to load your accounts'));
  }, []);

  const loadAccount = useCallback(async (id) => {
    if (!id) return;
    try {
      const r = await organisationsAPI.get(id);
      setAccount(r.data);
      setNameDraft(r.data.name);
      if (RANK[r.data.your_role] >= RANK.admin) {
        const inv = await invitesAPI.list({ organisation_id: id });
        setInvites(inv.data);
      } else {
        setInvites([]);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load the account');
    }
  }, []);

  useEffect(() => { loadAccounts(); }, [loadAccounts]);
  useEffect(() => { loadAccount(selectedId); }, [selectedId, loadAccount]);

  const myRole = account?.your_role;
  const isAdmin = RANK[myRole] >= RANK.admin;
  const isOwner = myRole === 'owner';

  function fail(err, fallback) {
    setError(err.response?.data?.error || fallback);
  }

  async function handleRename(e) {
    e.preventDefault();
    try {
      await organisationsAPI.rename(account.id, nameDraft.trim());
      setRenaming(false);
      loadAccount(account.id);
      loadAccounts();
    } catch (err) { fail(err, 'Failed to rename the account'); }
  }

  async function handleRoleChange(member, role) {
    if (role === member.role) return;
    try {
      await organisationsAPI.updateMemberRole(account.id, member.id, role);
      if (member.id === user.id) await refreshSession();
      loadAccount(account.id);
    } catch (err) { fail(err, 'Failed to change the role'); }
  }

  async function handleRemove(member) {
    const leaving = member.id === user.id;
    const prompt = leaving
      ? `Leave ${account.name}? You will lose access to its workspaces.`
      : `Remove ${member.name || member.email} from ${account.name}? Their seat comes off the bill.`;
    if (!confirm(prompt)) return;
    try {
      await organisationsAPI.removeMember(account.id, member.id);
      if (leaving) {
        await refreshSession();
        setParams({});
        setAccount(null);
        loadAccounts();
      } else {
        loadAccount(account.id);
      }
    } catch (err) { fail(err, leaving ? 'Failed to leave the account' : 'Failed to remove the member'); }
  }

  async function handleInvite(e) {
    e.preventDefault();
    if (!inviteEmail.trim()) return;
    setInviting(true);
    setError('');
    try {
      const r = await invitesAPI.create({ kind: 'member', organisation_id: account.id, email: inviteEmail.trim(), role: inviteRole });
      setIssued({ email: r.data.email, link: r.data.link });
      setInviteEmail('');
      setInviteRole('member');
      loadAccount(account.id);
    } catch (err) { fail(err, 'Failed to create the invite'); }
    finally { setInviting(false); }
  }

  async function handleWithdraw(invite) {
    if (!confirm(`Withdraw the invite for ${invite.email}? The link stops working.`)) return;
    try {
      await invitesAPI.withdraw(invite.id);
      setInvites(prev => prev.filter(i => i.id !== invite.id));
    } catch (err) { fail(err, 'Failed to withdraw the invite'); }
  }

  async function handleCreateWorkspace(e) {
    e.preventDefault();
    if (!newWorkspace.trim()) return;
    try {
      await organisationsAPI.createWorkspace(account.id, newWorkspace.trim());
      setNewWorkspace('');
      loadAccount(account.id);
    } catch (err) { fail(err, err.response?.data?.message || 'Failed to create the workspace'); }
  }

  async function handleDeleteWorkspace(ws) {
    const contents = ws.show_count
      ? `its ${ws.show_count} show${ws.show_count !== 1 ? 's' : ''} and everything in them`
      : 'everything in it';
    const typed = prompt(
      `Delete ${ws.name} and ${contents}? This cannot be undone, and its guests lose access.\n\n` +
      'Type the workspace name to confirm.'
    );
    if (typed === null) return;
    if (typed.trim() !== ws.name.trim()) {
      setError(`The name didn't match, so ${ws.name} was not deleted.`);
      return;
    }
    setError('');
    try {
      await organisationsAPI.deleteWorkspace(account.id, ws.id);
      // The open workspace is gone: the session lands somewhere still reachable.
      if (ws.id === user?.workspace_id) await refreshSession();
      loadAccount(account.id);
      loadAccounts();
    } catch (err) { fail(err, 'Failed to delete the workspace'); }
  }

  async function handleOpenWorkspace(workspaceId) {
    try {
      const r = await workspaceAPI.switch(workspaceId);
      setAuth(r.data.token, r.data.user);
      navigate('/');
    } catch (err) { fail(err, 'Failed to open the workspace'); }
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      <div className="max-w-2xl mx-auto px-6 py-16">
        <div className="flex items-center gap-3 mb-8">
          <button onClick={() => navigate('/')} className="text-gray-600 hover:text-white transition-colors" title="Back to shows">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 bg-red-600 rounded flex items-center justify-center">
              <div className="w-2 h-2 bg-white rounded-full" />
            </div>
            <span className="font-bold">Mission Control</span>
          </div>
          <h1 className="text-lg font-semibold ml-2">Account</h1>

          {accounts?.length > 1 && (
            <select
              value={selectedId || ''}
              onChange={e => { setIssued(null); setParams({ org: e.target.value }); }}
              className={`ml-auto ${input}`}
            >
              {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          )}
        </div>

        {error && (
          <div className="bg-red-900/30 border border-red-700 rounded px-4 py-3 text-red-300 text-sm mb-6">{error}</div>
        )}

        {accounts === null ? (
          <div className="text-gray-600 text-sm">Loading…</div>
        ) : accounts.length === 0 ? (
          <CreateOwnAccount
            onCreated={(session) => {
              setAuth(session.token, session.user);
              navigate('/billing?welcome=1');
            }}
          />
        ) : !account ? (
          <div className="text-gray-600 text-sm">Loading…</div>
        ) : (
          <>
            {/* Summary */}
            <div className={card}>
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  {renaming ? (
                    <form onSubmit={handleRename} className="flex gap-2 mb-1">
                      <input value={nameDraft} onChange={e => setNameDraft(e.target.value)} autoFocus className={input} />
                      <button type="submit" disabled={!nameDraft.trim()} className={primaryBtn}>Save</button>
                      <button type="button" onClick={() => setRenaming(false)} className="text-xs text-gray-500 hover:text-white">Cancel</button>
                    </form>
                  ) : (
                    <div className="text-2xl font-bold mb-1 flex items-center gap-3">
                      <span className="truncate">{account.name}</span>
                      {isAdmin && (
                        <button onClick={() => setRenaming(true)} className="text-xs font-normal text-gray-500 hover:text-white">Rename</button>
                      )}
                    </div>
                  )}
                  <div className="text-sm text-gray-500">
                    You are {myRole === 'owner' ? 'the owner' : `${myRole === 'admin' ? 'an' : 'a'} ${ROLE_LABELS[myRole].toLowerCase()}`}
                    {' · '}<span className="capitalize">{account.subscription_status}</span>
                  </div>
                  <div className="text-sm text-gray-300 mt-3">
                    {account.seats} seat{account.seats !== 1 ? 's' : ''} × {money(account.seat_price)}/{account.seat_price.interval}
                    {' = '}<span className="text-white font-medium">{money({ ...account.seat_price, amount: account.monthly_total })}/{account.seat_price.interval}</span>
                  </div>
                  <p className="text-xs text-gray-600 mt-1">Guests are free and do not take a seat.</p>
                </div>
                {isOwner && (
                  <button onClick={() => navigate(`/billing?org=${account.id}`)} className={`flex-shrink-0 ${quietBtn}`}>
                    Billing
                  </button>
                )}
              </div>
            </div>

            {/* Members */}
            <div className={card}>
              <h2 className={heading}>Members ({account.members.length})</h2>
              <div className="space-y-1 mb-4">
                {account.members.map(member => {
                  const isMe = member.id === user.id;
                  const canEditRole = isAdmin && !isMe && (isOwner || member.role !== 'owner');
                  const canRemove = isMe || (isAdmin && (isOwner || member.role !== 'owner'));
                  return (
                    <div key={member.id} className="flex items-center gap-3 px-3 py-2 bg-[#0a0a0a] border border-[#1a1a1a] rounded">
                      <Avatar person={member} />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-white truncate">
                          {member.name || member.email}{isMe && <span className="text-gray-500"> (you)</span>}
                        </div>
                        {member.name && <div className="text-xs text-gray-500 truncate">{member.email}</div>}
                      </div>
                      {canEditRole ? (
                        <select
                          value={member.role}
                          onChange={e => handleRoleChange(member, e.target.value)}
                          className="bg-[#1a1a1a] border border-[#2a2a2a] rounded px-2 py-1 text-xs text-gray-300"
                        >
                          {isOwner && <option value="owner">Owner</option>}
                          <option value="admin">Admin</option>
                          <option value="member">Member</option>
                        </select>
                      ) : (
                        <span className={`text-xs px-2 py-0.5 rounded font-medium ${member.role === 'member' ? 'bg-[#2a2a2a] text-gray-400' : 'bg-blue-500/20 text-blue-400'}`}>
                          {ROLE_LABELS[member.role]}
                        </span>
                      )}
                      {canRemove && (
                        <button
                          onClick={() => handleRemove(member)}
                          className="text-xs text-gray-500 hover:text-red-400 transition-colors"
                          title={isMe ? 'Leave this account' : 'Remove from the account'}
                        >
                          {isMe ? 'Leave' : 'Remove'}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>

              {isAdmin && (
                <>
                  {issued ? (
                    <IssuedInviteLink issued={issued} onDone={() => setIssued(null)} />
                  ) : (
                    <form onSubmit={handleInvite} className="space-y-2">
                      <div className="text-xs text-gray-500">
                        Invite a member — adds a seat at {money(account.seat_price)}/{account.seat_price.interval} once they accept.
                      </div>
                      <div className="flex gap-2">
                        <input
                          type="email"
                          value={inviteEmail}
                          onChange={e => setInviteEmail(e.target.value)}
                          placeholder="name@example.com"
                          className={`flex-1 ${input}`}
                        />
                        <select value={inviteRole} onChange={e => setInviteRole(e.target.value)} className={input}>
                          <option value="member">Member</option>
                          <option value="admin">Admin</option>
                        </select>
                        <button type="submit" disabled={inviting || !inviteEmail.trim()} className={primaryBtn}>
                          {inviting ? 'Inviting…' : 'Invite'}
                        </button>
                      </div>
                    </form>
                  )}

                  {invites.length > 0 && (
                    <div className="mt-4">
                      <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Pending invites</div>
                      <div className="space-y-1">
                        {invites.map(invite => (
                          <div key={invite.id} className="flex items-center gap-3 px-3 py-2 bg-[#0a0a0a] border border-[#1a1a1a] rounded">
                            <div className="flex-1 min-w-0 text-sm text-gray-300 truncate">{invite.email}</div>
                            <span className="text-xs text-gray-500">{ROLE_LABELS[invite.role] || 'Member'}</span>
                            <span className={`text-xs ${invite.expired ? 'text-red-400' : 'text-gray-600'}`}>
                              {invite.expired ? 'Expired' : `Until ${new Date(invite.expires_at).toLocaleDateString()}`}
                            </span>
                            <button onClick={() => handleWithdraw(invite)} className="text-xs text-gray-500 hover:text-red-400">Withdraw</button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Workspaces */}
            <div className={card}>
              <h2 className={heading}>Workspaces ({account.workspaces.length})</h2>
              <p className="text-xs text-gray-600 mb-3">Every member of the account can open every workspace. Guests are added per workspace, in Workspace Settings.</p>
              <div className="space-y-1 mb-4">
                {account.workspaces.map(ws => (
                  <div key={ws.id} className="flex items-center gap-3 px-3 py-2 bg-[#0a0a0a] border border-[#1a1a1a] rounded">
                    <div className="flex-1 min-w-0 text-sm text-white truncate">
                      {ws.name}
                      {ws.id === user?.workspace_id && <span className="text-xs text-blue-400 ml-2">open</span>}
                    </div>
                    <span className="text-xs text-gray-500">
                      {ws.show_count} show{ws.show_count !== 1 ? 's' : ''} · {ws.guest_count} guest{ws.guest_count !== 1 ? 's' : ''}
                    </span>
                    {ws.id !== user?.workspace_id && (
                      <button onClick={() => handleOpenWorkspace(ws.id)} className="text-xs text-gray-400 hover:text-white">Open</button>
                    )}
                    {/* An account keeps at least one workspace; the server refuses it too. */}
                    {isAdmin && account.workspaces.length > 1 && (
                      <button onClick={() => handleDeleteWorkspace(ws)} className="text-xs text-gray-500 hover:text-red-400 transition-colors">Delete</button>
                    )}
                  </div>
                ))}
              </div>
              {isAdmin && (
                <form onSubmit={handleCreateWorkspace} className="flex gap-2">
                  <input value={newWorkspace} onChange={e => setNewWorkspace(e.target.value)} placeholder="New workspace name" className={`flex-1 ${input}`} />
                  <button type="submit" disabled={!newWorkspace.trim()} className={primaryBtn}>Create workspace</button>
                </form>
              )}
            </div>

            {/* A member of someone else's account can still open their own. */}
            {!accounts.some(a => a.your_role === 'owner') && (
              <p className="text-center text-xs text-gray-600">
                Want an account of your own as well?{' '}
                <button
                  onClick={async () => {
                    const name = prompt('Name your account');
                    if (!name?.trim()) return;
                    try {
                      const r = await organisationsAPI.create(name.trim());
                      setAuth(r.data.token, r.data.user);
                      navigate('/billing?welcome=1');
                    } catch (err) { fail(err, 'Could not create the account'); }
                  }}
                  className="underline hover:text-gray-400"
                >
                  Set one up
                </button>
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
